/**
 * Spec field utilities for reading from and writing to OpenAPI spec objects
 * using dot-path notation. These are the low-level primitives used by
 * typed selectors and the wizard store actions.
 */

/**
 * Generic dot-path reader with type parameter.
 * Returns undefined if the path does not resolve.
 * Handles array indices (e.g., 'servers.0.url').
 */
export function readSpecField<T>(
  spec: Record<string, unknown> | null,
  path: string
): T | undefined {
  if (!spec) return undefined;

  const segments = path.split('.');
  let current: unknown = spec;

  for (const segment of segments) {
    if (current === null || current === undefined) return undefined;

    if (typeof current !== 'object') return undefined;

    // Handle array index access
    const index = Number(segment);
    if (Array.isArray(current) && !Number.isNaN(index)) {
      current = current[index];
    } else {
      current = (current as Record<string, unknown>)[segment];
    }
  }

  return current as T;
}

/**
 * Generic dot-path writer. Produces a NEW spec object (immutable update).
 * Creates intermediate objects/arrays as needed.
 */
export function writeSpecField(
  spec: Record<string, unknown>,
  path: string,
  value: unknown
): Record<string, unknown> {
  const segments = path.split('.');

  // Deep clone the spec to avoid mutation
  const result = structuredClone(spec);

  // Navigate to the parent of the target and set the value
  let current: unknown = result;

  for (let i = 0; i < segments.length - 1; i++) {
    const segment = segments[i];
    const nextSegment = segments[i + 1];
    const nextIsIndex = !Number.isNaN(Number(nextSegment));

    if (Array.isArray(current)) {
      const index = Number(segment);
      if (current[index] === undefined || current[index] === null) {
        current[index] = nextIsIndex ? [] : {};
      }
      current = current[index];
    } else {
      const obj = current as Record<string, unknown>;
      if (obj[segment] === undefined || obj[segment] === null) {
        obj[segment] = nextIsIndex ? [] : {};
      }
      current = obj[segment];
    }
  }

  // Set the final value
  const lastSegment = segments[segments.length - 1];
  if (Array.isArray(current)) {
    const index = Number(lastSegment);
    current[index] = value;
  } else {
    (current as Record<string, unknown>)[lastSegment] = value;
  }

  return result;
}

/**
 * Convenience reader for root-level tags array -> string[].
 * Reads from spec.tags (array of {name: string}) and returns the names.
 */
export function readSpecTags(spec: Record<string, unknown> | null): string[] {
  if (!spec) return [];

  const tags = spec.tags;
  if (!Array.isArray(tags)) return [];

  return tags
    .filter(
      (tag): tag is { name: string } =>
        typeof tag === 'object' &&
        tag !== null &&
        typeof (tag as Record<string, unknown>).name === 'string'
    )
    .map((tag) => tag.name);
}

/**
 * Convenience writer for root-level tags from string array.
 * Converts string[] to [{name: string}] format and writes to spec.tags.
 * Preserves existing tag descriptions if present.
 */
export function writeSpecTags(
  spec: Record<string, unknown>,
  tagNames: string[]
): Record<string, unknown> {
  // Build a lookup of existing tag descriptions
  const existingTags = Array.isArray(spec.tags)
    ? (spec.tags as Array<Record<string, unknown>>)
    : [];

  const descriptionMap = new Map<string, Record<string, unknown>>();
  for (const tag of existingTags) {
    if (typeof tag === 'object' && tag !== null && typeof tag.name === 'string') {
      const extra: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(tag)) {
        if (key !== 'name') {
          extra[key] = val;
        }
      }
      if (Object.keys(extra).length > 0) {
        descriptionMap.set(tag.name, extra);
      }
    }
  }

  // Build new tags array preserving existing descriptions
  const newTags = tagNames.map((name) => {
    const existing = descriptionMap.get(name);
    if (existing) {
      return { name, ...existing };
    }
    return { name };
  });

  return writeSpecField(spec, 'tags', newTags);
}
